import React, { useState, useRef, RefObject, useEffect, useMemo } from 'react';
import { format, toDate, utcToZonedTime } from 'date-fns-tz';
import useClickOutside from 'hooks/useClickOutside';
import { getUserTimeZone, isUSTimeZone } from 'common/helpers/v1';
import { DisplayDateRangeButton } from './DisplayDateRangeButton';
import { DateRangePickerSettingsProps, DisplayDateRangePicker } from './DisplayDateRangePicker';
import { DateRangeProps, useDateRange } from 'components/StyledUi/DateRange';
import { DateButtonWithDropdownProps } from './DisplayButtonWithDropdown/DateButtonWithDropdown.types';
import { useWidgetDateRange } from '../providers/useWidgetDateRange';
import { getTzOffset } from 'components/StyledUi/DateRange/hooks/useDateRangeV2';
import { DateButtonWithDropdownContainer } from 'components/StyledUi/DateRange/DateButtonWithDropdown/DateButtonWithDropdown.elements';


export const DisplayDateButton = ({ hideTimeInputs, hideDateRangeSelect, hideTimeRangeSelect, hideCalendar }: DateRangePickerSettingsProps): JSX.Element => {

  // This date range button can be used with both useWidgetDateRange OR useDateRange
  const { setDateRange: setDateRange1, startTime, endTime, isInit, timeZone: timeZone1 } = useWidgetDateRange();
  const { utcDateRange: dateRange1, setDateRange: setDateRange2, timeZone: timeZone2 } = useDateRange();

  const timezone = isInit ? timeZone1 : timeZone2

  const newDateRange = isInit ? {
    startTime: utcToZonedTime(startTime, timezone),
    endTime: utcToZonedTime(endTime, timezone)
  } : {
    startTime: utcToZonedTime(dateRange1?.startTime, timezone),
    endTime: utcToZonedTime(dateRange1?.endTime, timezone)
  }

  const setDateRange = ({ startTime, endTime }:DateRangeProps) => {
    if(isInit){
      setDateRange1({ startTime: startTime.toISOString(), endTime: endTime.toISOString() })
    } else {
      const offsetStringMachine = getTzOffset(timezone);
      const startMachine = toDate(format(startTime, `yyyy-MM-dd'T'HH:mm:ss`) + offsetStringMachine)
      const endMachine = toDate(format(endTime, `yyyy-MM-dd'T'HH:mm:ss`) + offsetStringMachine)
      setDateRange2({ startTime: startMachine, endTime: endMachine})
    }
  }

  const settings = {
    dateRange: newDateRange,
    setDateRange,
    hideTimeInputs, hideDateRangeSelect, hideTimeRangeSelect, hideCalendar
  }

  return <DisplayDateButtonWithDropdown {...settings}/>
}





const RefUser = (ref: RefObject<HTMLDivElement>, cb: (x?: boolean) => void) =>
  useClickOutside(ref, () => cb(false));

export const DisplayDateButtonWithDropdown = ({
  className,
  ga,
  dateRange,
  setDateRange,
  alignDropdown,
  iconOnly,
  hideIcon,
  placeholder,
  hasGoBackDateLimit,
  hideTimeInputs,
  hideDateRangeSelect,
  hideTimeRangeSelect,
  hideCalendar,
  dateFormat,
  hasFutureDates,
  timeZone
}: DateButtonWithDropdownProps): JSX.Element => {
  /** setting wrapping div css class */
  className = className ? `${className} ui-date-button-w-dropdown` : `ui-date-button-w-dropdown`;

  if (iconOnly) className += ` icon-only`;
  if (hideIcon) className += ` icon-hidden`;
  /** show/hide calendar dropdown */
  const [showCalendar, toggleCalendar] = useState(false);
  const [key, setKey] = useState(0);

  /** add custom class if calendar is open for extra styling */
  className = showCalendar ? `${className} is-open` : className;

  /** change formatting of button if the time select options are hidden */
  if (hideTimeRangeSelect) dateFormat = dateFormat || 'date-only';

  //this is to force DisplayDateRangeButton to re-mount when date range changes
  useEffect(() => {
    setKey((c) => c + 1);
  }, [dateRange]);

  const buttonSettings = {
    handleClick: () => toggleCalendar(!showCalendar),
    iconOnly,
    placeholder,
    dateFormat,
    dateRange,
    isUSTimeZone: isUSTimeZone(getUserTimeZone()),
    timeZone
  }

  const rangePickerSettings = {
    handleSubmit: (range: DateRangeProps) => {
      setDateRange(range);
      toggleCalendar(false);
    },
    handleCancel: () => toggleCalendar(false),
    dateRange,
    hasGoBackDateLimit,
    hideTimeInputs,
    hideDateRangeSelect,
    hideTimeRangeSelect,
    hideCalendar,
    hasFutureDates,
    timezone: timeZone
  };

  const ref = useRef<HTMLDivElement>(null);

  if (ref) RefUser(ref, () => toggleCalendar(false));

  return (
    <DateButtonWithDropdownContainer {...{ className, ga, alignDropdown }} ref={ref}>
      <DisplayDateRangeButton key={key} {...buttonSettings} />
      {showCalendar && <DisplayDateRangePicker {...rangePickerSettings} />}
    </DateButtonWithDropdownContainer>
  );
};
